<template>
  <v-container class="dashboard-wrap">
    <!-- Welcome Header -->
    <div class="d-flex align-center justify-space-between mb-6 flex-wrap ga-4">
      <div>
        <h1 class="text-h4 font-weight-bold">
          Hi, {{ auth.user?.displayName || auth.user?.username }}
        </h1>
        <div class="d-flex align-center ga-2 mt-1">
          <v-icon
            :color="sub.isActive ? 'primary' : 'grey'"
            size="18"
          >
            {{ sub.isActive ? "mdi-crown" : "mdi-crown-outline" }}
          </v-icon>
          <span class="text-body-2 text-grey">
            {{ auth.user?.isAdmin ? "Admin" : sub.isActive ? "Premium" : "Free" }}
            <template v-if="sub.isActive && sub.periodEnd">
              &middot; {{ sub.isCanceling ? "Expires" : "Renews" }}
              {{ sub.periodEnd }}
            </template>
          </span>
        </div>
      </div>
      <div class="d-flex ga-2">
        <v-chip v-if="auth.user?.isAdmin" color="primary" variant="flat">
          <v-icon start>mdi-shield-crown</v-icon>
          Admin
        </v-chip>
        <v-btn
          v-else-if="sub.isActive"
          variant="tonal"
          color="primary"
          @click="sub.openPortal()"
        >
          <v-icon start>mdi-credit-card-outline</v-icon>
          Manage subscription
        </v-btn>
        <v-btn v-else color="primary" to="/pricing">
          <v-icon start>mdi-crown</v-icon>
          Upgrade to Premium
        </v-btn>
      </div>
    </div>

    <!-- Stats Cards -->
    <v-row class="mb-6">
      <v-col v-for="stat in stats" :key="stat.label" cols="6" md="3">
        <v-card color="surface-variant" variant="flat" class="pa-4 text-center">
          <v-icon :color="stat.color" size="28" class="mb-2">
            {{ stat.icon }}
          </v-icon>
          <div class="text-h5 font-weight-bold">{{ stat.value }}</div>
          <div class="text-caption text-grey">{{ stat.label }}</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Upgrade Banner -->
    <v-card
      v-if="!sub.isActive"
      variant="tonal"
      color="primary"
      class="pa-6 text-center mb-6"
    >
      <v-icon size="36" class="mb-2">mdi-crown</v-icon>
      <h3 class="text-h6 mb-2">Upgrade to Premium</h3>
      <p class="text-body-2 mb-4">
        Upload games and get AI analysis.
      </p>
      <v-btn color="primary" to="/pricing">See pricing</v-btn>
    </v-card>

    <!-- Recent Games -->
    <h2 class="text-h5 font-weight-bold mb-4">Recent games</h2>

    <v-progress-circular
      v-if="games.loading"
      indeterminate
      color="primary"
      class="d-block mx-auto my-6"
    />

    <v-card v-else-if="games.games.length === 0" color="surface-variant" variant="flat" class="pa-8 text-center">
      <v-icon size="48" color="grey" class="mb-4">mdi-gamepad-variant-outline</v-icon>
      <h3 class="text-h6 mb-2">No games yet</h3>
      <p class="text-body-2 text-grey">
        Upload your first game from the app to get started.
      </p>
    </v-card>

    <v-list v-else bg-color="surface" rounded="lg">
      <v-list-item
        v-for="game in games.games"
        :key="game.gameId"
        class="cursor-pointer"
      >
        <v-list-item-title>{{ game.title || "Untitled game" }}</v-list-item-title>
        <v-list-item-subtitle>{{ formatDate(game.playedAt) || "—" }}</v-list-item-subtitle>

        <template v-slot:append>
          <v-chip
            :color="resultColor(game.result)"
            size="small"
            variant="flat"
            class="mr-2"
          >
            {{ resultLabel(game.result) }}
            <span v-if="game.winType" class="ml-1 text-caption">
              ({{ game.winType }})
            </span>
          </v-chip>
          <v-chip
            v-if="game.analysisStatus"
            :color="analysisColor(game.analysisStatus)"
            size="x-small"
            variant="tonal"
          >
            {{ game.analysisStatus }}
          </v-chip>
        </template>
      </v-list-item>
    </v-list>

    <!-- Cancel Dialog -->
    <v-dialog v-model="showCancelDialog" max-width="400">
      <v-card class="pa-6">
        <h3 class="text-h6 mb-4">Cancel subscription?</h3>
        <p class="text-body-2 mb-2">
          You keep access until {{ sub.periodEnd }}.
        </p>
        <p class="text-body-2 text-grey mb-4">
          You will lose access to AI analysis and game upload.
        </p>
        <div class="d-flex justify-end ga-2">
          <v-btn variant="text" @click="showCancelDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="confirmCancel">Cancel subscription</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useAuthStore } from "../stores/auth";
import { useSubscriptionStore } from "../stores/subscription";
import { useGamesStore } from "../stores/games";

const auth = useAuthStore();
const sub = useSubscriptionStore();
const games = useGamesStore();

const showCancelDialog = ref(false);

const stats = [
  { label: "Spil", value: games.pagination.total || 0, icon: "mdi-gamepad-variant", color: "primary" },
  { label: "Sejre", value: "—", icon: "mdi-trophy", color: "success" },
  { label: "Tab", value: "—", icon: "mdi-emoticon-sad", color: "error" },
  { label: "Streak", value: "—", icon: "mdi-fire", color: "orange" },
];

const resultColor = (r) =>
  ({ win: "success", loss: "error", draw: "grey" }[r] || "grey");
const resultLabel = (r) =>
  ({ win: "Sejr", loss: "Tab", draw: "Uafgjort", unknown: "?" }[r] || "?");
const analysisColor = (s) =>
  ({ complete: "success", processing: "warning", pending: "grey", failed: "error" }[s] || "grey");
const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString("da-DK", { day: "numeric", month: "short" }) : null;

async function confirmCancel() {
  await sub.cancel();
  showCancelDialog.value = false;
}

onMounted(async () => {
  await sub.fetchStatus();
  if (sub.isActive) games.fetchGames();
});
</script>

<style scoped>
.dashboard-wrap {
  max-width: 1100px;
  margin: 0 auto;
  padding-top: 2rem;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
